import type { DataPoint, MetricTrend } from '@/types/core';
import type { DashboardMetrics, DashboardTopContributor } from '@/types/dashboard';
import type { OrgFileHotspot, ReviewPair } from '@/types/insights';

// Mock data for top contributors (matching DashboardTopContributor interface)
export const MOCK_TOP_CONTRIBUTORS: DashboardTopContributor[] = [
  {
    developer_id: 'dev1',
    external_login: 'acme-dev1',
    name: 'Alex Developer',
    avatar_url: 'https://avatars.githubusercontent.com/u/1?s=64',
    loc_per_day: 150,
  },
  {
    developer_id: 'dev2',
    external_login: 'acme-dev2',
    name: 'Bob Engineer',
    avatar_url: 'https://avatars.githubusercontent.com/u/2?s=64',
    loc_per_day: 130,
  },
  {
    developer_id: 'dev3',
    external_login: 'acme-dev3',
    name: 'Charlie Coder',
    avatar_url: 'https://avatars.githubusercontent.com/u/3?s=64',
    loc_per_day: 110,
  },
  {
    developer_id: 'dev4',
    external_login: 'acme-dev4',
    name: 'David Debugger',
    avatar_url: 'https://avatars.githubusercontent.com/u/4?s=64',
    loc_per_day: 100,
  },
  {
    developer_id: 'dev5',
    external_login: 'acme-dev5',
    name: 'Eve Reviewer',
    avatar_url: 'https://avatars.githubusercontent.com/u/5?s=64',
    loc_per_day: 90,
  },
];

export const MOCK_REVIEW_PAIRS: ReviewPair[] = [
  { reviewer_id: 'dev1', reviewer_login: 'acme-dev1', reviewer_name: 'Alex', author_id: 'dev2', author_login: 'acme-dev2', author_name: 'Bob', review_count: 15, reviewer_avatar: 'https://avatars.githubusercontent.com/u/1?s=64', author_avatar: 'https://avatars.githubusercontent.com/u/2?s=64' },
  { reviewer_id: 'dev2', reviewer_login: 'acme-dev2', reviewer_name: 'Bob', author_id: 'dev1', author_login: 'acme-dev1', author_name: 'Alex', review_count: 12, reviewer_avatar: 'https://avatars.githubusercontent.com/u/2?s=64', author_avatar: 'https://avatars.githubusercontent.com/u/1?s=64' },
  { reviewer_id: 'dev1', reviewer_login: 'acme-dev1', reviewer_name: 'Alex', author_id: 'dev3', author_login: 'acme-dev3', author_name: 'Charlie', review_count: 8, reviewer_avatar: 'https://avatars.githubusercontent.com/u/1?s=64', author_avatar: 'https://avatars.githubusercontent.com/u/3?s=64' },
  { reviewer_id: 'dev3', reviewer_login: 'acme-dev3', reviewer_name: 'Charlie', author_id: 'dev4', author_login: 'acme-dev4', author_name: 'David', review_count: 20, reviewer_avatar: 'https://avatars.githubusercontent.com/u/3?s=64', author_avatar: 'https://avatars.githubusercontent.com/u/4?s=64' },
  { reviewer_id: 'dev4', reviewer_login: 'acme-dev4', reviewer_name: 'David', author_id: 'dev5', author_login: 'acme-dev5', author_name: 'Eve', review_count: 18, reviewer_avatar: 'https://avatars.githubusercontent.com/u/4?s=64', author_avatar: 'https://avatars.githubusercontent.com/u/5?s=64' },
  { reviewer_id: 'dev5', reviewer_login: 'acme-dev5', reviewer_name: 'Eve', author_id: 'dev2', author_login: 'acme-dev2', author_name: 'Bob', review_count: 5, reviewer_avatar: 'https://avatars.githubusercontent.com/u/5?s=64', author_avatar: 'https://avatars.githubusercontent.com/u/2?s=64' },
  { reviewer_id: 'dev2', reviewer_login: 'acme-dev2', reviewer_name: 'Bob', author_id: 'dev4', author_login: 'acme-dev4', author_name: 'David', review_count: 7, reviewer_avatar: 'https://avatars.githubusercontent.com/u/2?s=64', author_avatar: 'https://avatars.githubusercontent.com/u/4?s=64' },
];

// Mock data for DashboardMetrics
export const MOCK_EXECUTIVE_SUMMARY_METRICS: DashboardMetrics = {
  organization_id: 'sample-org',
  period: '30d',
  cycle_time_hours: {
    metric_name: 'Cycle Time',
    current_value: 50,
    previous_value: 60,
    change_percent: -16.67,
    trend: 'down',
  },
  prs_merged: {
    metric_name: 'PRs Merged',
    current_value: 120,
    previous_value: 100,
    change_percent: 20,
    trend: 'up',
  },
  review_coverage: {
    metric_name: 'Review Coverage',
    current_value: 85,
    previous_value: 80,
    change_percent: 6.25,
    trend: 'up',
  },
  test_failure_rate: {
    metric_name: 'Test Failure Rate',
    current_value: 7,
    previous_value: 10,
    change_percent: -30,
    trend: 'down',
  },
  deployment_frequency: {
    metric_name: 'Deployment Frequency',
    current_value: 1.5,
    previous_value: 1.2,
    change_percent: 25,
    trend: 'up',
  },
  merge_without_approval_rate: {
    metric_name: 'Merge Without Approval Rate',
    current_value: 8,
    previous_value: 12,
    change_percent: -33.33,
    trend: 'down',
  },
  coding_time_hours: {
    metric_name: 'Coding Time',
    current_value: 15,
    trend: 'stable',
  },
  wait_for_review_hours: {
    metric_name: 'Pickup Time',
    current_value: 5,
    trend: 'up',
  },
  review_to_approval_hours: {
    metric_name: 'Review Time',
    current_value: 28,
    trend: 'down',
  },
  approval_to_merge_hours: {
    metric_name: 'Merge Time',
    current_value: 2,
    trend: 'stable',
  },
  mttr_hours: {
    metric_name: 'Mean Time to Recovery',
    current_value: 8,
    previous_value: 10,
    change_percent: -20,
    trend: 'down',
  },
  top_contributors: MOCK_TOP_CONTRIBUTORS,
  total_contributors: MOCK_TOP_CONTRIBUTORS.length,
};

// Generate realistic trend data for the last 30 days
function generateTrendData(
  baseValue: number,
  variance: number,
  trend: 'improving' | 'stable' | 'declining' = 'stable'
): DataPoint[] {
  const data: DataPoint[] = [];
  const today = new Date();

  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);

    // Add some randomness with overall trend direction
    const trendFactor = trend === 'improving' ? -0.02 : trend === 'declining' ? 0.02 : 0;
    const dayVariance = (Math.random() - 0.5) * variance;
    const trendAdjustment = (29 - i) * trendFactor * baseValue;
    const value = Math.max(0, baseValue + dayVariance + trendAdjustment);

    data.push({
      date: date.toISOString().split('T')[0],
      value: Math.round(value * 100) / 100,
    });
  }

  return data;
}

// Mock trend data for charts
export const MOCK_CYCLE_TIME_TREND: MetricTrend = {
  metric_name: 'cycle_time_hours',
  data_points: generateTrendData(50, 20, 'improving'),
};

export const MOCK_PRS_MERGED_TREND: MetricTrend = {
  metric_name: 'prs_merged',
  data_points: generateTrendData(4, 3, 'stable'), // ~4 PRs per day
};

export const MOCK_REVIEW_COVERAGE_TREND: MetricTrend = {
  metric_name: 'review_coverage_percent',
  data_points: generateTrendData(85, 10, 'improving'),
};

export const MOCK_LINES_CHANGED_TREND: MetricTrend = {
  metric_name: 'total_lines_changed',
  data_points: generateTrendData(500, 200, 'stable'), // ~500 lines changed per day on average
};

export const MOCK_AVG_LINES_PER_DEV_TREND: MetricTrend = {
  metric_name: 'avg_lines_per_active_dev',
  data_points: generateTrendData(125, 40, 'stable'), // ~125 lines per active dev per day
};

// Mock repositories for demo
export const MOCK_REPOSITORIES = [
  {
    id: 'demo-repo-1',
    organization_id: 'sample-org',
    external_id: '12345',
    name: 'frontend-app',
    full_name: 'acme-corp/frontend-app',
    default_branch: 'main',
    is_private: true,
    language: 'TypeScript',
    last_synced_at: new Date().toISOString(),
    last_sync_status: 'completed' as const,
    initial_sync_completed: true,
    created_at: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'demo-repo-2',
    organization_id: 'sample-org',
    external_id: '12346',
    name: 'backend-api',
    full_name: 'acme-corp/backend-api',
    default_branch: 'main',
    is_private: true,
    language: 'Python',
    last_synced_at: new Date().toISOString(),
    last_sync_status: 'completed' as const,
    initial_sync_completed: true,
    created_at: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'demo-repo-3',
    organization_id: 'sample-org',
    external_id: '12347',
    name: 'shared-libs',
    full_name: 'acme-corp/shared-libs',
    default_branch: 'main',
    is_private: true,
    language: 'TypeScript',
    last_synced_at: new Date().toISOString(),
    last_sync_status: 'completed' as const,
    initial_sync_completed: true,
    created_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString(),
  },
];

export const MOCK_GHOST_AND_STARS = [
  {
    name: 'Kevin Park',
    primary_lang: 'Python',
    type: 'ghost',
    daily_loc: 17,
    pr_count: 7,
    review_count: 4,
    status: 'At Risk',
  },
  {
    name: 'Maya Chen',
    primary_lang: 'TypeScript',
    type: 'star',
    daily_loc: 420,
    pr_count: 45,
    review_count: 82,
    status: 'Promote',
  },
  {
    name: 'David Okonkwo',
    primary_lang: 'Go',
    type: 'star',
    daily_loc: 210,
    pr_count: 32,
    review_count: 156,
    status: 'Retain',
  },
];

/**
 * Illustrative file-hotspot data for the /code-hotspots marketing landing page.
 * Not real customer data — representative of typical churn/risk distribution.
 */
export const MOCK_FILE_HOTSPOTS: OrgFileHotspot[] = [
  {
    filename: 'src/payments/processor.ts',
    repository_id: 'mock-repo-1',
    repository_name: 'billing-service',
    change_count: 187,
    total_lines_changed: 9420,
    unique_contributors: 2,
    contributor_logins: ['aisha', 'dmitri'],
    contributor_ids: ['m1', 'm2'],
    risk_level: 'high',
    trend_direction: 'up',
    trend_percentage: 34,
    previous_change_count: 139,
    weekly_changes: [4, 6, 9, 7, 12, 15, 18],
  },
  {
    filename: 'src/auth/session-manager.ts',
    repository_id: 'mock-repo-1',
    repository_name: 'billing-service',
    change_count: 142,
    total_lines_changed: 6110,
    unique_contributors: 3,
    contributor_logins: ['aisha', 'sam', 'lena'],
    contributor_ids: ['m1', 'm3', 'm4'],
    risk_level: 'high',
    trend_direction: 'up',
    trend_percentage: 21,
    previous_change_count: 117,
    weekly_changes: [3, 5, 6, 8, 9, 11, 13],
  },
  {
    filename: 'src/api/orders-controller.ts',
    repository_id: 'mock-repo-2',
    repository_name: 'orders-api',
    change_count: 98,
    total_lines_changed: 4380,
    unique_contributors: 4,
    contributor_logins: ['sam', 'lena', 'dmitri', 'priya'],
    contributor_ids: ['m3', 'm4', 'm2', 'm5'],
    risk_level: 'medium',
    trend_direction: 'stable',
    trend_percentage: 3,
    previous_change_count: 95,
    weekly_changes: [6, 7, 6, 8, 7, 8, 7],
  },
  {
    filename: 'src/checkout/cart.ts',
    repository_id: 'mock-repo-2',
    repository_name: 'orders-api',
    change_count: 76,
    total_lines_changed: 2950,
    unique_contributors: 5,
    contributor_logins: ['priya', 'sam', 'lena', 'noah', 'aisha'],
    contributor_ids: ['m5', 'm3', 'm4', 'm6', 'm1'],
    risk_level: 'medium',
    trend_direction: 'down',
    trend_percentage: 12,
    previous_change_count: 86,
    weekly_changes: [10, 9, 8, 7, 6, 6, 5],
  },
  {
    filename: 'src/notifications/email.ts',
    repository_id: 'mock-repo-3',
    repository_name: 'web-app',
    change_count: 41,
    total_lines_changed: 1320,
    unique_contributors: 6,
    contributor_logins: ['noah', 'priya', 'sam', 'lena', 'dmitri', 'aisha'],
    contributor_ids: ['m6', 'm5', 'm3', 'm4', 'm2', 'm1'],
    risk_level: 'low',
    trend_direction: 'stable',
    trend_percentage: 1,
    previous_change_count: 40,
    weekly_changes: [6, 5, 6, 6, 5, 6, 7],
  },
  {
    filename: 'src/utils/format.ts',
    repository_id: 'mock-repo-3',
    repository_name: 'web-app',
    change_count: 28,
    total_lines_changed: 540,
    unique_contributors: 7,
    contributor_logins: ['noah', 'priya', 'sam', 'lena', 'dmitri', 'aisha', 'kai'],
    contributor_ids: ['m6', 'm5', 'm3', 'm4', 'm2', 'm1', 'm7'],
    risk_level: 'low',
    trend_direction: 'down',
    trend_percentage: 8,
    previous_change_count: 30,
    weekly_changes: [5, 4, 4, 3, 4, 4, 4],
  },
];
